/**
 * Workflow Canvas graph data model — 7 fixed primitives, nothing else.
 * Ref: 04_Frontend_Canvas_Rule_To_Workflow_Changes.md §3
 */

export type PrimitiveType =
  | 'START'
  | 'END'
  | 'CALL_API'
  | 'CLASSIFY'
  | 'NOTIFY'
  | 'WAIT_SIGNAL'
  | 'BRANCH'
  | 'AGENT';

export const PRIMITIVE_TYPES: PrimitiveType[] = [
  'START',
  'END',
  'CALL_API',
  'CLASSIFY',
  'NOTIFY',
  'WAIT_SIGNAL',
  'BRANCH',
  'AGENT',
];

export const PRIMITIVE_LABELS: Record<PrimitiveType, string> = {
  START: 'Start',
  END: 'End',
  CALL_API: 'Call API',
  CLASSIFY: 'Classify',
  NOTIFY: 'Notify',
  WAIT_SIGNAL: 'Wait for Signal',
  BRANCH: 'Branch',
  AGENT: 'Agent',
};

export const PRIMITIVE_COLORS: Record<PrimitiveType, string> = {
  START: '#2e7d32',
  END: '#616161',
  CALL_API: '#1565c0',
  CLASSIFY: '#6a1b9a',
  NOTIFY: '#ef6c00',
  WAIT_SIGNAL: '#ad1457',
  BRANCH: '#00838f',
  AGENT: '#4527a0',
};

/**
 * dataTransfer MIME types shared between WorkflowPalette (drag source) and the canvas drop
 * target in WorkflowCanvasTab — kept in one place so both sides can't drift apart.
 */
export const DRAG_MIME_PRIMITIVE = 'application/x-workflow-primitive';
export const DRAG_MIME_ACTION = 'application/x-workflow-action';

export interface GraphNode {
  id: string;
  /** Omit entirely for an action-authored node (action_id set instead) — the server fills this in on compile. */
  type?: PrimitiveType;
  name: string;
  /** NEW — read-only, server-computed. Always a good, non-empty, human-readable header string
   * (falls back to a humanized primitive type if `name` is blank). Never sent back on save —
   * the server recomputes it fresh on every response, so it can't go stale. */
  display_label?: string;
  /** Omit entirely for an action-authored node — the server fills this in on compile. */
  config?: Record<string, unknown>;
  /** NEW — set to author this node via an Action Catalog entry instead of a raw primitive (ref: 06_...md §3). */
  action_id?: string;
  /** NEW — friendly field values for the action's `definition.inputs` (ref: 06_...md §3). */
  inputs?: Record<string, unknown>;
  /** true when an LLM design guessed this field — render with an ASSUMED badge */
  assumed?: boolean;
  /** React Flow layout position, round-tripped, ignored by the execution engine */
  position: { x: number; y: number };
}

/**
 * Action Catalog — friendly business actions that compile to primitives server-side.
 * Ref: 06_Frontend_API_Changes_Action_Catalog_2026-07-20.md §2, §3.
 */
export interface CatalogActionInput {
  key: string;
  label: string;
  type: string;
  required?: boolean;
  /** Present when type === 'enum' — the fixed set of valid values, rendered as a dropdown
   * (ref: 17_Frontend_Custom_API_Request_Path_Dropdown_2026-08-03.md). */
  options?: string[];
  /** Present when type === 'dynamic_multiselect' (dynamic_multiselect_FE_datasource_picker_spec.md
   * §3.1) — tells FE which live-listing endpoint to call instead of a baked-in option list.
   * `endpoint` is read by DynamicMultiselectField rather than hardcoding a URL, so BS/ORCH can
   * repoint it without an FE deploy (only 'datasources' is wired to a real backend today).
   * `scope_param`/`scope_field` are accepted for forward-compat with the spec's documented shape
   * but unused today — this field's actual scope is the enclosing channel (an ambient prop, not
   * a sibling form field), see DynamicMultiselectField's own doc comment. */
  source?: { endpoint: string; scope_param?: string; scope_field?: string };
  /** PR #670 review (M1) — the compile-time default the orchestrator falls back to
   * (`_resolve_template` in workflow_compile.py) when this field has never been touched and so
   * has no entry in `inputs`. Needed so, e.g., a `boolean` field whose catalog default is `true`
   * doesn't render OFF in the UI while the run actually behaves as ON underneath. */
  default?: unknown;
}

export interface CatalogActionOutput {
  key: string;
  label: string;
}

export interface CatalogAction {
  action_id: string;
  version: number;
  label: string;
  category: string;
  description?: string;
  connection_id?: string;
  compiles_to: PrimitiveType;
  /** `definition.template` is internal/compile-only — never render or depend on it (ref: §2). */
  definition: {
    inputs: CatalogActionInput[];
    outputs: CatalogActionOutput[];
  };
}

/** `GET /catalog/actions` response shape — actions grouped by category, i.e. the palette data. */
export type CatalogActionsByCategory = Record<string, CatalogAction[]>;

/** `GET /catalog/connections` response entry — never carries credentials. */
export interface CatalogConnection {
  connection_id: string;
  label: string;
  base_url?: string;
  scope: string;
  status: string;
}

/**
 * FIX2_FE_numeric_branch_comparison_spec.md §2.1/§3.1 — the BRANCH comparison operators.
 * PR #669 review (N3) — '!=' added: the orchestrator's BranchComparisonOp includes it, but this
 * spec's original five didn't, so a loaded edge with op: "!=" failed isValidBranchCondition and
 * silently opened in plain-text mode with a blank label instead of the structured view.
 */
export type EdgeComparisonOperator = '==' | '!=' | '>' | '<' | '>=' | '<=';

/**
 * PR #669 review (C1) — corrected shape: the email_orchestrator schema
 * (app/schemas/workflow_config.py) declares a numeric BRANCH comparison as its OWN field,
 * `condition_op: Optional[BranchCondition]`, separate from `condition: Optional[str]` — never an
 * object embedded in `condition` itself (that was this branch's first, incorrect attempt).
 * `field` names the context value being compared (e.g. "days_requested") and is required by the
 * backend schema; the FE previously had no input for it at all.
 */
export interface BranchCondition {
  op: EdgeComparisonOperator;
  field: string;
  value: number;
}

export interface GraphEdge {
  id: string;
  source: string;
  target: string;
  /** null for an unconditional edge; a plain string for the legacy equality format (e.g.
   * "approved"), set only on edges leaving a BRANCH (or a WAIT_SIGNAL timeout path). Never an
   * object — see condition_op below for the structured numeric comparison. */
  condition: string | null;
  /** PR #669 review (C1/C2) — structured numeric BRANCH comparison, matching the orchestrator's
   * own `condition_op` field name and `BranchCondition` shape exactly. As of this fix, the
   * orchestrator's validator explicitly rejects any edge carrying this ("condition_op is not
   * supported by the workflow runtime yet") until its interpreter ships — see
   * NUMERIC_BRANCH_CONDITIONS_ENABLED in edgeConditionUtils.ts, which gates creating NEW ones
   * behind that readiness without hiding/stripping one that already exists on a loaded edge. */
  condition_op?: BranchCondition | null;
}

export interface WorkflowConfigDocument {
  name: string;
  version?: number;
  status?: string;
  created_by?: string;
  created_at?: string;
  nodes: GraphNode[];
  edges: GraphEdge[];
}

export interface WorkflowVersionSummary {
  version: number;
  status: string;
  created_by: string;
  created_at: string;
}

/** Default config fields per primitive — used when adding a new node from the palette. */
export function defaultConfigFor(type: PrimitiveType): Record<string, unknown> {
  switch (type) {
    case 'START':
    case 'END':
      return {};
    case 'CALL_API':
      return { method: 'GET', path: '', body: {}, params: {}, result_key: '' };
    case 'CLASSIFY':
      return { input_key: '', result_key: '' };
    case 'NOTIFY':
      return {
        template_id: '',
        subject: '',
        body: '',
        recipient_email_key: '',
        variables: {},
        cc_emails: [],
        bcc_emails: [],
      };
    case 'WAIT_SIGNAL':
      return { signal_name: '', timeout_hours: 24, timeout_condition: 'timeout', result_key: '' };
    case 'BRANCH':
      return { condition_key: '', default_condition: '' };
    case 'AGENT':
      return { prompt: '', result_key: '' };
    default:
      return {};
  }
}
